import React, { FC, useEffect, useState } from 'react';
import InputField from '../../input-field';
import DropdownField from '../../dropdown-field';
import Spacer from '../../../shared/tds/components/@atoms/spacer';
import Spacings from '../../../shared/tds/components/tokens/layout/spacings';
import CheckboxField from '../../checkbox-field';
import Button from '../../button';
import {
  BulkRmiContact,
  BulkRmiInput,
  BulkRmiResponse,
} from '../../../hooks/use-create-bulk-rmi-request';
import Address from '../../../types/address';
import useStates from '../../../hooks/use-states';
import useCountries from '../../../hooks/use-countries';
import BulkRmiRequest from '../../../types/BulkRmiRequest';

interface Props {
  submit: (input: BulkRmiInput) => void;
  submitLoading: boolean;
  submitErrorMessage: string;
  submitResponse: BulkRmiResponse | null;
  request?: BulkRmiRequest;
}

const CreateRequestForm: FC<Props> = ({
  submit,
  submitLoading,
  submitErrorMessage,
  submitResponse,
  request,
}) => {
  const [companyAddress, setCompanyAddress] = useState<Address>({
    company: '',
    address1: '',
    address2: '',
    attention: '',
    city: '',
    country: 'US',
    county: '',
    email: '',
    phone: '',
    postalCode: '',
    state: '',
  });
  const [vendorCode, setVendorCode] = useState<string>('');
  const [contact, setContact] = useState<BulkRmiContact>({
    name: '',
    email: '',
  });

  const accessPackageOptionsList = [
    {
      id: 'CONTENT_OPTION_PARTS',
      label: 'Content Option Parts - $9,411',
    },
    {
      id: 'CONTENT_OPTION_SERVICE',
      label: 'Content Option Service - $17,200',
    },
    {
      id: 'CONTENT_OPTION_DIAGNOSTICS',
      label: 'Content Option Diagnostics - $34,275',
    },
  ];
  const [accessPackageOptions, setAccessPackageOptions] = useState<string[]>(
    []
  );

  const marketPackageOptionsList = [
    {
      id: 'US_&_CANADA',
      label: 'U.S. & Canada (Americas Region)',
    },
    {
      id: 'EMEA',
      label: 'EMEA (Europe Region)',
    },
    {
      id: 'APAC',
      label: 'APAC (Asia Pacific)',
    },
  ];
  const [marketPackageOptions, setMarketPackageOptions] = useState<string[]>(
    []
  );

  const [productListDescription, setProductListDescription] =
    useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<BulkRmiResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const states = useStates();
  const countries = useCountries();

  useEffect(() => {
    if (request) {
      const {
        companyAddress,
        vendorCode,
        contact,
        packageOptions,
        productListDescription,
      } = request;
      setCompanyAddress({
        company: companyAddress.company,
        address1: companyAddress.address1,
        address2: companyAddress.address2,
        attention: companyAddress.attention,
        city: companyAddress.city,
        country: companyAddress.country,
        county: companyAddress.county,
        email: companyAddress.email,
        phone: companyAddress.phone,
        postalCode: companyAddress.postalCode,
        state: companyAddress.state,
      });
      setVendorCode(vendorCode);
      setContact({
        name: contact.name,
        email: contact.email,
      });

      // ensure access package options are still available
      const accessOptions: string[] = [];
      packageOptions.forEach((po) => {
        if (accessPackageOptionsList.some((o) => o.label === po)) {
          accessOptions.push(po);
        }
      });
      setAccessPackageOptions(accessOptions);

      // ensure market package options are still available
      const marketOptions: string[] = [];
      packageOptions.forEach((po) => {
        if (marketPackageOptionsList.some((o) => o.label === po)) {
          marketOptions.push(po);
        }
      });
      setMarketPackageOptions(marketOptions);

      setProductListDescription(productListDescription);
    }
  }, [request]);

  useEffect(() => {
    setLoading(submitLoading);
  }, [submitLoading]);

  useEffect(() => {
    setErrorMessage(submitErrorMessage);
  }, [submitErrorMessage]);

  useEffect(() => {
    setResponse(submitResponse);
    setLoading(false);
  }, [submitResponse]);

  const mapCountryToOption = (country) => {
    return {
      label: country.name,
      value: country.code,
    };
  };

  const mapStateToOption = (state) => {
    return {
      label: state.name,
      value: state.stateCode,
    };
  };

  const countryOptions = countries
    .filter((c) => c.code === 'US' || c.code === 'CA')
    .map(mapCountryToOption);

  const renderStateField = () => {
    const stateOptions = states
      .filter((s) => s.countryCode === companyAddress?.country)
      .map(mapStateToOption);

    return (
      <React.Fragment>
        {stateOptions.length > 1 ? (
          <DropdownField
            id="state"
            label="State/Province"
            value={companyAddress.state}
            options={[{ label: '- Choose One -', value: '' }, ...stateOptions]}
            onChange={(e) => {
              const ca = { ...companyAddress };
              ca.state = e.target.value;
              setCompanyAddress(ca);
            }}
            required={true}
          />
        ) : (
          <InputField
            id="state"
            label="State/Province"
            type="text"
            maxLength={2}
            defaultValue={companyAddress.state}
            onChange={(e) => {
              const ca = { ...companyAddress };
              ca.state = e.target.value;
              setCompanyAddress(ca);
            }}
            required={true}
          />
        )}
      </React.Fragment>
    );
  };

  const onAccessPackageOptionChange = (e, option: string) => {
    let newOptions = [...accessPackageOptions.filter((o) => o !== option)];

    if (e.target.checked) {
      newOptions.push(option);
    }

    setAccessPackageOptions(newOptions);
  };

  const onMarketPackageOptionChange = (e, option: string) => {
    let newOptions = [...marketPackageOptions.filter((o) => o !== option)];

    if (e.target.checked) {
      newOptions.push(option);
    }

    setMarketPackageOptions(newOptions);
  };

  const onSubmitClicked = async () => {
    setLoading(true);
    setResponse(null);
    setErrorMessage('');

    if (!companyAddress.company) {
      setLoading(false);
      setErrorMessage('Company name is required');
      return;
    }

    if (!companyAddress.country) {
      setLoading(false);
      setErrorMessage('Company country is required');
      return;
    }

    if (!companyAddress.address1) {
      setLoading(false);
      setErrorMessage('Company address 1 is required');
      return;
    }

    if (!companyAddress.city) {
      setLoading(false);
      setErrorMessage('Company city is required');
      return;
    }

    if (!companyAddress.state) {
      setLoading(false);
      setErrorMessage('Company state/province is required');
      return;
    }

    if (!companyAddress.postalCode) {
      setLoading(false);
      setErrorMessage('Company postal code is required');
      return;
    }

    if (!vendorCode) {
      setLoading(false);
      setErrorMessage('Vendor code is required');
      return;
    }

    if (!contact.name) {
      setLoading(false);
      setErrorMessage('Contact name is required');
      return;
    }

    if (!contact.email) {
      setLoading(false);
      setErrorMessage('Contact email is required');
      return;
    }

    if (accessPackageOptions.length === 0) {
      setLoading(false);
      setErrorMessage('At least one access package option is required');
      return;
    }

    if (marketPackageOptions.length === 0) {
      setLoading(false);
      setErrorMessage('At least one market package option is required');
      return;
    }

    if (!productListDescription) {
      setLoading(false);
      setErrorMessage('Product list description is required');
      return;
    }

    const packageOptions = [...accessPackageOptions].concat([
      ...marketPackageOptions,
    ]);

    const input: BulkRmiInput = {
      companyAddress,
      vendorCode,
      contact,
      packageOptions,
      productListDescription,
    };

    await submit(input);
  };

  return (
    <React.Fragment>
      <h2>Company:</h2>
      <p>US and Canada only</p>
      <InputField
        id="companyName"
        label="Company Name:"
        type="text"
        defaultValue={companyAddress.company}
        onChange={(e) => {
          const ca = { ...companyAddress };
          ca.company = e.target.value;
          setCompanyAddress(ca);
        }}
        required={true}
        error={null}
      />
      <DropdownField
        id="country"
        label="Country"
        options={countryOptions}
        value={companyAddress.country}
        onChange={(e) => {
          const ca = { ...companyAddress };
          ca.country = e.target.value;
          ca.state = '';
          setCompanyAddress(ca);
        }}
        required={true}
      />
      <InputField
        id="address1"
        label="Address 1:"
        type="text"
        defaultValue={companyAddress.address1}
        onChange={(e) => {
          const ca = { ...companyAddress };
          ca.address1 = e.target.value;
          setCompanyAddress(ca);
        }}
        required={true}
        error={null}
      />
      <InputField
        id="address2"
        label="Address 2:"
        type="text"
        defaultValue={companyAddress.address2}
        onChange={(e) => {
          const ca = { ...companyAddress };
          ca.address2 = e.target.value;
          setCompanyAddress(ca);
        }}
        error={null}
      />
      <InputField
        id="city"
        label="City:"
        type="text"
        defaultValue={companyAddress.city}
        onChange={(e) => {
          const ca = { ...companyAddress };
          ca.city = e.target.value;
          setCompanyAddress(ca);
        }}
        required={true}
        error={null}
      />
      {renderStateField()}
      <InputField
        id="postalCode"
        label="Postal Code:"
        type="text"
        defaultValue={companyAddress.postalCode}
        onChange={(e) => {
          const ca = { ...companyAddress };
          ca.postalCode = e.target.value;
          setCompanyAddress(ca);
        }}
        required={true}
        error={null}
      />
      <Spacer spacing={Spacings.small} orientation="vertical" />
      <h2>Vendor:</h2>
      <p>
        If you do not have a Vendor Code, please contact{' '}
        <a href="mailto:VendorCodeSetup@volvotechinfo.com">
          VendorCodeSetup@volvotechinfo.com
        </a>{' '}
        to request a code.
      </p>
      <InputField
        id="vendorCode"
        label="Vendor Code:"
        type="text"
        defaultValue={vendorCode}
        onChange={(e) => setVendorCode(e.target.value)}
        required={true}
        error={null}
      />
      <Spacer spacing={Spacings.small} orientation="vertical" />
      <h2>Contact:</h2>
      <InputField
        id="contactName"
        label="Contact Name:"
        type="text"
        defaultValue={contact.name}
        onChange={(e) => {
          const c = { ...contact };
          c.name = e.target.value;
          setContact(c);
        }}
        required={true}
        error={null}
      />
      <InputField
        id="contactEmail"
        label="Email:"
        type="text"
        defaultValue={contact.email}
        onChange={(e) => {
          const c = { ...contact };
          c.email = e.target.value;
          setContact(c);
        }}
        required={true}
        error={null}
      />
      <Spacer spacing={Spacings.small} orientation="vertical" />
      <h2>
        Access Package - $34,275 (must choose at least one option below) *
      </h2>
      {accessPackageOptionsList.map((option) => (
        <CheckboxField
          key={option.id}
          id={option.id}
          label={option.label}
          checked={accessPackageOptions.some((po) => po === option.label)}
          onChange={(e) => onAccessPackageOptionChange(e, option.label)}
        />
      ))}
      <p>Plus $11,425 one time start-up fee if a new customer.</p>
      <Spacer spacing={Spacings.small} orientation="vertical" />
      <h2>Market Package (must choose at least one option below) *</h2>
      <p>
        BULK RMI is available for both U.S./Canada Region and Europe Region.
        They are priced the same for both markets, however they will be charged
        separately.
      </p>
      {marketPackageOptionsList.map((option) => (
        <CheckboxField
          key={option.id}
          id={option.id}
          label={option.label}
          checked={marketPackageOptions.some((po) => po === option.label)}
          onChange={(e) => onMarketPackageOptionChange(e, option.label)}
        />
      ))}
      <Spacer spacing={Spacings.small} orientation="vertical" />
      <h2>
        List the products that will be integrating the RMI data (these are the
        only products you will be authorized to integrate): *
      </h2>
      <InputField
        id="reason"
        label=""
        type="text"
        multiline={true}
        defaultValue={productListDescription}
        onChange={(e) => setProductListDescription(e.target.value)}
      />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <Button cy="submit" text="Submit" onClick={onSubmitClicked} />
      )}
      {response && (
        <p className={response.statusCode != 200 ? 'error' : ''}>
          {response.message}
        </p>
      )}
      {errorMessage && <p className="error">{errorMessage}</p>}
      <style jsx>{`
        .error {
          color: red;
        }
      `}</style>
    </React.Fragment>
  );
};

export default CreateRequestForm;
